/**
 * TutorialHintPanel - Non-blocking FTUE hint display
 *
 * Renders at bottom-center, above the command dock.
 * Shows contextual hints from the server, with dismiss and skip buttons.
 */

import Phaser from 'phaser';
import { EventBus } from '../../utils/EventBus';
import { MMOEvents } from '../../managers/MMONetworkManager';
import {
  Colors, CSSColors, Fonts, FontSize, Radius, Anim, Depth,
} from './GameUITheme';
import { I18n } from '../../i18n/I18nService';
import { isMobile } from '../../utils/ResponsiveHelper';

const PANEL_W = 420;
const PANEL_H = 52;
const PANEL_PAD = 12;
const ICON_SIZE = 20;

interface FTUEGuidance {
  stage: string;
  hintText: string;
  highlightType?: string | null;
  highlightTarget?: string | null;
}

export class TutorialHintPanel extends Phaser.GameObjects.Container {
  private bg!: Phaser.GameObjects.Graphics;
  private hintText!: Phaser.GameObjects.Text;
  private dismissBtn!: Phaser.GameObjects.Container;
  private skipBtn!: Phaser.GameObjects.Container;
  private isVisible = false;
  private pulseTarget: Phaser.GameObjects.Graphics | null = null;
  private panelW: number;

  constructor(scene: Phaser.Scene) {
    const { width, height } = scene.cameras.main;
    // On mobile the hint anchors to the TOP (below the DOM top bar + any mode
    // banner) to stay clear of the CommandDock's 110px bottom zone. Desktop
    // keeps the original bottom-center placement.
    const startY = isMobile(scene) ? -PANEL_H : height + PANEL_H;
    super(scene, width / 2, startY); // Start off-screen

    this.panelW = isMobile(scene) ? Math.min(PANEL_W, width - 16) : PANEL_W;
    this.setDepth(Depth.overlay);

    // Build panel
    this.createPanel();

    scene.add.existing(this);

    // Listen for FTUE events
    EventBus.on(MMOEvents.FTUE_GUIDANCE, this.onGuidance, this);
    EventBus.on(MMOEvents.FTUE_STAGE_UPDATE, this.onStageUpdate, this);
    EventBus.on(MMOEvents.FTUE_COMPLETED, this.onCompleted, this);
  }

  private createPanel(): void {
    const pw = this.panelW;

    // Background
    this.bg = this.scene.add.graphics();
    this.bg.fillStyle(Colors.bgPanel, 0.95);
    this.bg.fillRoundedRect(-pw / 2, -PANEL_H / 2, pw, PANEL_H, Radius.lg);
    this.bg.lineStyle(1, Colors.cyan, 0.4);
    this.bg.strokeRoundedRect(-pw / 2, -PANEL_H / 2, pw, PANEL_H, Radius.lg);
    this.add(this.bg);

    // Compass/tutorial icon
    const icon = this.scene.add.text(
      -pw / 2 + PANEL_PAD, 0,
      '\u2737', // six-pointed star
      { fontSize: FontSize.lg, fontFamily: Fonts.body, color: CSSColors.cyan },
    ).setOrigin(0, 0.5);
    this.add(icon);

    // Hint text
    this.hintText = this.scene.add.text(
      -pw / 2 + PANEL_PAD + ICON_SIZE + 8, 0,
      '',
      {
        fontSize: FontSize.sm,
        fontFamily: Fonts.body,
        color: CSSColors.textWhite,
        wordWrap: { width: pw - PANEL_PAD * 2 - ICON_SIZE - 80 },
      },
    ).setOrigin(0, 0.5);
    this.add(this.hintText);

    // Dismiss [X] button
    this.dismissBtn = this.createButton(I18n.t('tutorial.dismiss'), pw / 2 - PANEL_PAD - 42, 0, () => {
      this.dismiss();
      EventBus.emit('send_message', { type: 'ftue_dismiss', payload: {} });
    });
    this.add(this.dismissBtn);

    // Skip button
    this.skipBtn = this.createButton(I18n.t('tutorial.skip'), pw / 2 - PANEL_PAD, 0, () => {
      this.onCompleted();
      EventBus.emit('send_message', { type: 'ftue_disable', payload: {} });
    });
    this.add(this.skipBtn);
  }

  private createButton(label: string, x: number, y: number, onClick: () => void): Phaser.GameObjects.Container {
    const container = this.scene.add.container(x, y);
    const isSmall = label === 'X';
    const btnW = isSmall ? 24 : 40;
    const btnH = 24;

    const bg = this.scene.add.graphics();
    bg.fillStyle(Colors.bgElevated, 0.8);
    bg.fillRoundedRect(-btnW / 2, -btnH / 2, btnW, btnH, Radius.sm);
    container.add(bg);

    const text = this.scene.add.text(0, 0, label, {
      fontSize: FontSize.xs,
      fontFamily: Fonts.body,
      color: CSSColors.textMuted,
    }).setOrigin(0.5);
    container.add(text);

    const hitZone = this.scene.add.zone(0, 0, btnW, btnH)
      .setInteractive({ useHandCursor: true });
    hitZone.on('pointerover', () => {
      text.setColor(CSSColors.textWhite);
      bg.clear();
      bg.fillStyle(Colors.bgCardHover, 1);
      bg.fillRoundedRect(-btnW / 2, -btnH / 2, btnW, btnH, Radius.sm);
    });
    hitZone.on('pointerout', () => {
      text.setColor(CSSColors.textMuted);
      bg.clear();
      bg.fillStyle(Colors.bgElevated, 0.8);
      bg.fillRoundedRect(-btnW / 2, -btnH / 2, btnW, btnH, Radius.sm);
    });
    hitZone.on('pointerdown', onClick);
    container.add(hitZone);

    return container;
  }

  private onGuidance(guidance: FTUEGuidance): void {
    this.showHint(guidance);
  }

  private onStageUpdate(guidance: FTUEGuidance): void {
    this.showHint(guidance);
  }

  private showHint(guidance: FTUEGuidance): void {
    if (!guidance || !guidance.hintText) return;

    this.hintText.setText(guidance.hintText);

    // Remove any existing pulse
    this.removePulse();

    // Show panel if hidden
    if (!this.isVisible) {
      this.isVisible = true;
      const { height } = this.scene.cameras.main;
      // Mobile: anchor to the TOP below the 44px DOM top bar + 6px safe margin
      //         + 56px mode banner allowance + 8px gap = y ≈ 140 (half-height added)
      // Desktop: bottom-center 80px up from the bottom, above the CommandDock.
      const targetY = isMobile(this.scene) ? 44 + 6 + 56 + 8 + PANEL_H / 2 : height - 80;
      this.scene.tweens.add({
        targets: this,
        y: targetY,
        duration: Anim.slow,
        ease: 'Back.easeOut',
      });
    }

    // Highlight target if specified
    if (guidance.highlightType && guidance.highlightTarget) {
      EventBus.emit('ftue-highlight', {
        type: guidance.highlightType,
        target: guidance.highlightTarget,
      });
    }
  }

  private dismiss(): void {
    if (!this.isVisible) return;
    this.isVisible = false;

    this.removePulse();

    const { height } = this.scene.cameras.main;
    // Slide back out the same side we entered from.
    const hideY = isMobile(this.scene) ? -PANEL_H : height + PANEL_H;
    this.scene.tweens.add({
      targets: this,
      y: hideY,
      duration: Anim.normal,
      ease: 'Power2',
    });
  }

  private onCompleted(): void {
    this.dismiss();
    this.removePulse();

    // Remove event listeners
    EventBus.off(MMOEvents.FTUE_GUIDANCE, this.onGuidance, this);
    EventBus.off(MMOEvents.FTUE_STAGE_UPDATE, this.onStageUpdate, this);
    EventBus.off(MMOEvents.FTUE_COMPLETED, this.onCompleted, this);
  }

  private removePulse(): void {
    if (this.pulseTarget) {
      this.pulseTarget.destroy();
      this.pulseTarget = null;
    }
  }

  destroy(fromScene?: boolean): void {
    EventBus.off(MMOEvents.FTUE_GUIDANCE, this.onGuidance, this);
    EventBus.off(MMOEvents.FTUE_STAGE_UPDATE, this.onStageUpdate, this);
    EventBus.off(MMOEvents.FTUE_COMPLETED, this.onCompleted, this);
    this.removePulse();
    super.destroy(fromScene);
  }
}
